// src/pages/ModelLoader.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ModelLoader.css';

// Environment configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

console.log("API_URL: ", API_URL);

// Component for empty model container
const EmptyModelContainer = () => {
    return (
        <div className="empty-model-container">
            <div>Please load an STL model to begin</div>
        </div>
    );
};

const ModelLoader = () => {
    const [selectedWrapper, setSelectedWrapper] = useState(null);
    const [selectedModelPath, setSelectedModelPath] = useState("");
    const [filenames, setFilenames] = useState([]);
    const [loadedModels, setLoadedModels] = useState([]);
    const [showAllModels, setShowAllModels] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Processing...");
    const navigate = useNavigate();

    const wrapperNames = ["Original", "Voxelized", "Alpha Reconstruction"];

    // Upload file to server for processing
    const uploadFile = async (file) => {
        setIsLoading(true);
        setLoadingMessage("Uploading model to server...");
        const formData = new FormData();
        formData.append('file', file);
            //console.log("Prepared to upload");

        const isProduction = !API_URL.includes('127.0.0.1') && !API_URL.includes('localhost');

        try {

            const response = await fetch(`${API_URL}/upload`, {
                method: 'POST',
                body: formData,
            });


            if (response.ok) {
                const data = await response.json();
                    //console.log("File uploaded successfully:", data);

                // Check if response contains direct URLs (GCS production mode)
                if (isProduction && data.original && data.original.startsWith('http')) {
                        //console.log("Using direct GCS URLs");

                    // Extract the actual filenames from the URLs to store as identifiers
                    const originalFilename = data.original.split('/').pop();
                    const voxelizedFilename = data.voxelized ? data.voxelized.split('/').pop() : null;
                    const reconstructedFilename = data.reconstructed ? data.reconstructed.split('/').pop() : null;

                    // Store the filenames 
                    setFilenames([
                        originalFilename,
                        voxelizedFilename || "voxelized.stl",
                        reconstructedFilename || "reconstructed.stl"
                    ]);

                    // Return all URLs and filenames as objects
                    return [
                        {
                            path: data.original,
                            filename: originalFilename
                        },
                        {
                            path: data.voxelized,
                            filename: voxelizedFilename || "voxelized.stl"
                        },
                        {
                            path: data.reconstructed,
                            filename: reconstructedFilename || "reconstructed.stl"
                        }
                    ];
                } else {
                    // Handle local development mode
                        //console.log("Using local development mode");

                    // For local development, use simple filenames without paths
                    const voxelizedName = data.voxelized || "voxelized.stl";
                    const reconstructedName = data.reconstructed || "reconstructed.stl";

                    setFilenames([
                        file.name,
                        voxelizedName,
                        reconstructedName
                    ]);

                        //console.log("Local filenames set:", file.name, voxelizedName, reconstructedName);

                    // Fetch the modified STL files
                    setLoadingMessage("Processing model variants...");
                    const voxelizedURL = await fetchFile(voxelizedName);
                    const reconstructedURL = await fetchFile(reconstructedName);

                    // Return objects with path and filename
                    return [
                        {
                            path: voxelizedURL,
                            filename: voxelizedName
                        },
                        {
                            path: reconstructedURL,
                            filename: reconstructedName
                        }
                    ];
                }
            } else {
                const error = await response.json();
                    //console.error('Error uploading file:', error);
                setIsLoading(false);
                alert("Error uploading file: " + (error.message || "Unknown error"));
                return null;
            }
        } catch (err) {
                //console.error('Error:', err);
            setIsLoading(false);
            console.log("Error: ", err);
            console.log("Connection error: " + (err.message || "Unknown error"));
            return null;
        }
    };

    // Fetch processed file from server
    const fetchFile = async (url) => {
        const isProduction = !API_URL.includes('127.0.0.1') && !API_URL.includes('localhost');

        // If URL is already a complete URL, return it directly
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                //console.log("Direct URL detected:", url);
            return url;
        }

        // Clean the URL
        const cleanUrl = url.startsWith('/') ? url.substring(1) : url;

        setLoadingMessage(`Downloading processed model: ${cleanUrl}...`);

        try {
            // Local development - use direct download endpoint
            const downloadUrl = `${API_URL}/${cleanUrl}`;
                //console.log("Fetching from:", downloadUrl);

            const response = await fetch(downloadUrl);

            // Handle redirects (for GCS URLs)
            if (response.redirected) {
                    //console.log("Redirect detected:", response.url);
                return response.url;
            }

            if (response.ok) {
                const blob = await response.blob();
                const fileURL = URL.createObjectURL(blob);
                return fileURL;
            } else {
                    //console.error("Failed to fetch file:", response.statusText);

                // Only try GCS fallback in production mode
                if (isProduction) {
                    const gcsUrl = `https://storage.googleapis.com/cameleon-models/${cleanUrl}`;
                        //console.log("Trying GCS fallback URL:", gcsUrl);

                    try {
                        const gcsResponse = await fetch(gcsUrl, { method: 'HEAD' });
                        if (gcsResponse.ok) {
                            return gcsUrl;
                        }
                    } catch (gcsError) {
                            //console.error("GCS fallback failed:", gcsError);
                    }
                }

                alert("Failed to download processed model: " + cleanUrl);
                return null;
            }
        } catch (error) {
                //console.error("Error fetching file:", error);
            alert("Error downloading model: " + error.message);
            return null;
        }
    };

    // Select a model wrapper
    const selectWrapper = (wrapper, filename) => {
        setSelectedWrapper(wrapper);
        setSelectedModelPath(filename);
            //console.log("Selected Model Path:", filename);
    };

    // Handle model file loading
    const loadSTLModel = async (file) => {
        setIsLoading(true);
        setLoadingMessage("Reading STL file...");

        const isProduction = !API_URL.includes('127.0.0.1') && !API_URL.includes('localhost');


        if (isProduction) {
            // In production, we upload directly to the server and use returned URLs
            try {
                // Process the model on the server
                const urlObjects = await uploadFile(file);

                if (urlObjects && urlObjects.length > 0) {
                    const newModels = [];

                    // In production, map the returned objects to our model structure
                    urlObjects.forEach((urlObj, index) => {
                        if (urlObj && urlObj.path) {
                            newModels.push({
                                path: urlObj.path,
                                title: wrapperNames[index], // Use correct index from wrapperNames
                                filename: urlObj.filename   // Use filename from the returned object
                            });
                        }
                    });

                    setLoadedModels(newModels);
                    setShowAllModels(false);

                    // Select the first model by default if available
                    if (newModels.length > 0) {
                        selectWrapper(0, newModels[0].filename);
                    }
                }
            } catch (error) {
                    //console.error("Error processing model in production:", error);
                alert("Error processing STL file: " + error.message);
            } finally {
                setIsLoading(false);
            }
        } else {
            // In development, we use blob URLs for the original model
            const reader = new FileReader();

            reader.onload = async function (event) {
                try {
                    const fileContent = event.target.result;

                    // Create a blob URL from the STL file content
                    const blob = new Blob([fileContent], { type: 'application/octet-stream' });
                    const fileURL = URL.createObjectURL(blob);

                    // Reset models and add original
                    setLoadedModels([{
                        path: fileURL,
                        title: wrapperNames[0],
                        filename: file.name
                    }]);

                    // Process the model on the server
                    const urls = await uploadFile(file);

                    if (urls) {
                        const newModels = [
                            {
                                path: fileURL,
                                title: wrapperNames[0],
                                filename: file.name
                            }
                        ];

                        urls.forEach((urlObj, index) => {
                            if (urlObj && urlObj.path) {
                                newModels.push({
                                    path: urlObj.path,
                                    title: wrapperNames[index + 1],
                                    filename: urlObj.filename
                                });
                            }
                        });

                        setLoadedModels(newModels);
                        setShowAllModels(false);
                    }

                    // Select the first model by default
                    selectWrapper(0, file.name);

                } catch (error) {
                        //console.error("Error loading model:", error);
                    alert("Error processing STL file: " + error.message);
                } finally {
                    setIsLoading(false);
                }
            };

            reader.onerror = function () {
                    //console.error("FileReader error");
                alert("Error reading the STL file");
                setIsLoading(false);
            };

            reader.readAsArrayBuffer(file);
        }
    };

    // Handle file input
    const handleFileSelect = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".stl";
        input.addEventListener("change", (event) => {
            const file = event.target.files[0];
            if (file) {
                loadSTLModel(file);
            }
        });
        input.click();
    };

    // Handle confirm button
    const handleConfirm = () => {
        if (selectedModelPath === "") {
            alert("Select one of the models");
        } else {
            // Find the selected model object from loadedModels to get the actual path
            const selectedModel = loadedModels.find((model, index) => index === selectedWrapper);

            if (selectedModel) {
                // Instead of passing the full blob URL, just pass a model ID or filename
                // This keeps the URL clean
                if (selectedModel.filename) {
                    navigate(`/?modelName=${encodeURIComponent(selectedModel.filename)}`);

                    // Save the full model path to localStorage - still useful as backup
                    localStorage.setItem('selectedSTLModel', selectedModel.path);
                    localStorage.setItem('selectedSTLModelName', selectedModel.filename);

                        //console.log("Confirmed model selection:", selectedModel.filename);
                } else {
                        //console.error("Selected model has no filename");
                }
            } else {
                    //console.error("Selected model not found in loadedModels array");
            }
        }
    };

    // Toggle showing all models
    const toggleShowAllModels = () => {
        setShowAllModels(!showAllModels);
    };
    return (
        <div className="model-loader-container">
            {/* Loading overlay */}
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <div className="loading-text">{loadingMessage}</div>
                    </div>
                </div>
            )}

            <div id="buttonContainer" style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '10px',
                padding: '10px'
            }}>
                <button
                    id="loadModel"
                    className="button-style"
                    onClick={handleFileSelect}
                    disabled={isLoading}
                    style={{
                        backgroundColor: isLoading ? '#cccccc' : '#3288e6',
                        color: 'white',
                        padding: '10px 15px',
                        borderRadius: '4px',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        border: 'none',
                        fontSize: '14px',
                        width: '120px',
                        textAlign: 'center'
                    }}
                >
                    {isLoading ? "Processing..." : "Load Model"}
                </button>
                <button
                    id="confirmButton"
                    className="button-style"
                    onClick={handleConfirm}
                    disabled={isLoading || selectedModelPath === ""}
                    style={{
                        backgroundColor: isLoading || selectedModelPath === "" ? '#cccccc' : '#3288e6',
                        color: 'white',
                        padding: '10px 15px',
                        borderRadius: '4px',
                        cursor: isLoading || selectedModelPath === "" ? 'not-allowed' : 'pointer',
                        border: 'none',
                        fontSize: '14px',
                        width: '120px',
                        textAlign: 'center'
                    }}
                >
                    Confirm
                </button>
                {loadedModels.length > 1 && (
                    <button
                        id="showrec"
                        className="button-style"
                        onClick={toggleShowAllModels}
                        disabled={isLoading}
                        style={{
                            backgroundColor: isLoading ? '#cccccc' : '#3288e6',
                            color: 'white',
                            padding: '10px 15px',
                            borderRadius: '4px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            border: 'none',
                            fontSize: '14px',
                            width: '120px',
                            textAlign: 'center'
                        }}
                    >
                        {showAllModels ? "Show Original" : "Show All"}
                    </button>
                )}
            </div>

            <div
                id="viewport-container"
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    padding: '10px',
                    height: 'calc(100vh - 70px - 30px)', /* Adjusted for fixed bottom bar */
                    minHeight: 'calc(100vh - 70px - 30px)', /* Adjusted for fixed bottom bar */
                    position: 'relative',
                    pointerEvents: isLoading ? 'none' : 'auto', // Disable interactions while loading
                    overflow: 'hidden'
                }}
            >
                {/* Empty placeholder container when no models are loaded */}
                {loadedModels.length === 0 && <EmptyModelContainer />}

                {/* Display loaded models */}
                {loadedModels.length > 0 && loadedModels.map((model, index) => (
                    // Only show the first model if showAllModels is false and index > 0
                    <ModelViewer
                        key={index}
                        model={model}
                        isSelected={selectedWrapper === index}
                        onSelect={() => selectWrapper(index, model.filename)}
                        isFullScreen={!showAllModels && index === 0}
                        hidden={!showAllModels && index > 0}
                        isLoading={isLoading}
                    />
                ))}
            </div>

            {/* Fixed controls bar at the bottom */}
            <div className="controls-bar">
                <strong>Controls:</strong> Left-click + drag: Rotate | Right-click + drag: Pan | Scroll: Zoom
            </div>
        </div>
    );
};

// Component to show model viewer
const ModelViewer = ({ model, isSelected, onSelect, isFullScreen, hidden, isLoading }) => {
    const iframeRef = useRef(null);
    const [modelUrl, setModelUrl] = useState(model.path);
    const [baseHtmlUrl, setBaseHtmlUrl] = useState('base.html');

    // Determine if we're in production mode by checking the API_URL
    useEffect(() => {
        const isProduction = !API_URL.includes('127.0.0.1') && !API_URL.includes('localhost');
        if (isProduction) {
            // Use the GCP bucket URL for base.html in production
            setBaseHtmlUrl('https://scaffolder.s3.us-east-1.amazonaws.com/viewer.html');
        } else {
            // Use the local base.html in development
            setBaseHtmlUrl('viewer.html');
        }
    }, []);

    // When model path changes, ensure we're using the proper URL format
    useEffect(() => {
        // If it's a GCS URL (contains storage.googleapis.com), ensure CORS is enabled
        // by proxying the request through our backend if needed
        if (model.path && model.path.includes('storage.googleapis.com') && !model.path.includes('cameleon-models')) {
            // This is a direct URL but missing the bucket name, try to use it directly
            setModelUrl(model.path);
        } else {
            // For all other URLs (blob URLs, backend URLs, etc.), use as is
            setModelUrl(model.path);
        }
    }, [model.path]);

    if (hidden) {
        return null;
    }

    return (
        <div
            className={`iframe-wrapper ${isSelected ? 'selected' : ''} ${isFullScreen ? 'full-screen' : ''}`}
            style={{
                flex: isFullScreen ? '1 0 100%' : '1',
                position: 'relative',
                height: 'calc(100vh - 40px)',
                border: isSelected ? '2px solid blue' : '1px solid #ddd',
                borderRadius: '4px',
                overflow: 'hidden',
                transition: 'border-color 0.3s',
                display: hidden ? 'none' : 'block',
                opacity: isLoading ? 0.7 : 1,
                pointerEvents: isLoading ? 'none' : 'auto' // Disable interactions while loading
            }}
        >
            <iframe
                ref={iframeRef}
                src={`${baseHtmlUrl}?model=${encodeURIComponent(modelUrl)}`}
                className="iframe-model"
                style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                }}
                title={model.title}
            />

            <div
                className="wrapper-title"
                style={{
                    position: 'absolute',
                    top: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 5,
                    background: 'rgba(255, 255, 255, 0.8)',
                    padding: '5px 10px',
                    fontFamily: 'sans-serif',
                    fontSize: '22px',
                    pointerEvents: 'none'
                }}
            >
                {model.title}
            </div>

            <button
                className={`select-button ${isSelected ? 'selected' : ''}`}
                onClick={onSelect}
                disabled={isLoading}
                style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 5,
                    background: isSelected ? 'rgba(200, 255, 200, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                    border: isSelected ? '1px solid #4caf50' : '1px solid #aaa',
                    padding: '5px 10px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.7 : 1,
                    color: isSelected ? '#2e7d32' : 'inherit'
                }}
            >
                {isSelected ? "Imported" : "Import"}
            </button>
        </div>
    );
};

export default ModelLoader;